import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Logo from "../assets/images/logo.png";
import "../style/assistantAuth.css";

// Harus sama dengan OTP_COOLDOWN_MS di backend — server diam-diam tidak
// mengirim ulang di dalam jeda ini, jadi tombolnya dikunci selama itu supaya
// assistant tidak menunggu email yang memang tidak dikirim.
const RESEND_COOLDOWN_SECONDS = 60;

// Tiga langkah: "email" → "otp" → "password". Dipakai juga untuk memasang
// password pertama kali, karena akun yang dibuat admin belum punya password.
const ForgotPassAssistant = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  // Hanya disimpan di state: hilang saat halaman di-refresh, dan memang
  // sebaiknya begitu — token ini cukup untuk mengganti password.
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [confPass, setConfPass] = useState("");
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const showError = (error, fallback) =>
    setMessage({ type: "error", text: error.response?.data?.msg || fallback });

  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    if (!email) return setMessage({ type: "error", text: "Please input your email" });
    setIsLoading(true);
    try {
      const res = await axios.post("/assistant/password/otp", { email });
      setMessage({ type: "info", text: res.data.msg });
      setOtp("");
      setStep("otp");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      showError(error, "Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return setMessage({ type: "error", text: "Enter the 6-digit code from your email" });
    setIsLoading(true);
    try {
      const res = await axios.post("/assistant/password/verify", { email, otp });
      setResetToken(res.data.resetToken);
      setMessage(null);
      setStep("password");
    } catch (error) {
      showError(error, "Failed to verify OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (password.length < 8) return setMessage({ type: "error", text: "Password must be at least 8 characters." });
    if (password !== confPass) return setMessage({ type: "error", text: "Your password doesn't match" });
    setIsLoading(true);
    try {
      const res = await axios.post("/assistant/password/reset", { email, resetToken, password, confPass });
      await Swal.fire({
        text: res.data.msg,
        icon: "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#0d9488",
      });
      navigate("/assistant/login");
    } catch (error) {
      showError(error, "Failed to set password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="assistant-auth-page">
      <div className="assistant-auth-card">
        <img className="assistant-auth-logo" src={Logo} alt="logo.png" />
        <p className="assistant-auth-title">Set / Reset Password</p>
        <p className="assistant-auth-subtitle">
          {step === "email" && "We will send a one-time code (OTP) to your email"}
          {step === "otp" && `Enter the 6-digit code sent to ${email}`}
          {step === "password" && "Choose a new password for your account"}
        </p>

        {message && (
          <p className={`assistant-auth-message ${message.type === "error" ? "is-error" : "is-info"}`}>{message.text}</p>
        )}

        {step === "email" && (
          <form onSubmit={requestOtp}>
            <div className="mb-3">
              <label className="form-label">Email address</label>
              <input
                type="email"
                className="form-control"
                placeholder="example@gmail.com"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <button type="submit" className="btn assistant-auth-btn" disabled={isLoading}>
              {isLoading ? "Sending..." : "Send OTP"}
            </button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={verifyOtp}>
            <div className="mb-3">
              <label className="form-label">OTP code</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="form-control assistant-auth-otp"
                placeholder="------"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
            </div>
            <button type="submit" className="btn assistant-auth-btn" disabled={isLoading}>
              {isLoading ? "Verifying..." : "Verify OTP"}
            </button>
            <button
              type="button"
              className="assistant-auth-link"
              disabled={cooldown > 0 || isLoading}
              onClick={() => requestOtp()}
            >
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
            </button>
          </form>
        )}

        {step === "password" && (
          <form onSubmit={savePassword}>
            <div className="mb-3">
              <label className="form-label">New password</label>
              <input
                type="password"
                className="form-control"
                placeholder="at least 8 characters"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Confirm new password</label>
              <input
                type="password"
                className="form-control"
                placeholder="repeat password"
                autoComplete="new-password"
                value={confPass}
                onChange={(e) => setConfPass(e.target.value)}
              />
            </div>
            <button type="submit" className="btn assistant-auth-btn" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save password"}
            </button>
          </form>
        )}

        <button type="button" className="assistant-auth-link" onClick={() => navigate("/assistant/login")}>
          Back to login
        </button>
      </div>
    </div>
  );
};

export default ForgotPassAssistant;
